import { useMutation, useQuery, useQueryClient } from 'react-query';
import { queryKey } from 'constants/queryKey';
import {
  WidgetsSetupApi,
  WidgetsSetupTarget,
} from 'app/modules/posts/api/widgetsSetup';
import { WidgetsSetup } from 'components/posts/detail/layout/widgetsSetup/WidgetsSetupProvider';

type Props = {
  target: WidgetsSetupTarget;
};

type WidgetExpansionState = {
  isExpanded: boolean;
  setExpanded: (expanded: boolean) => void;
};

type WidgetsExpansionState = {
  [key: string]: WidgetExpansionState;
};

const DEFAULT_WIDGET_SETUP: Record<WidgetsSetupTarget, WidgetsSetup[]> = {
  'post-preview': [
    { key: 'localPosts', isExpanded: true },
    { key: 'localPost', isExpanded: true },
    { key: 'communication', isExpanded: true },
    { key: 'postCompletion', isExpanded: true },
    { key: 'publishingMethod', isExpanded: false },
    { key: 'translation', isExpanded: false },
    { key: 'labels', isExpanded: false },
    { key: 'notes', isExpanded: false },
    { key: 'boostBudget', isExpanded: false },
    { key: 'postRequirements', isExpanded: false },
    { key: 'tasks', isExpanded: false },
  ],
  'post-edit': [
    { key: 'communication', isExpanded: true },
    { key: 'postContent', isExpanded: true },
    { key: 'publishingMethod', isExpanded: false },
    { key: 'postPlanning', isExpanded: false },
    { key: 'localPosts', isExpanded: true },
    { key: 'boostBudget', isExpanded: false },
    { key: 'labels', isExpanded: false },
    { key: 'notes', isExpanded: false },
    { key: 'postRequirements', isExpanded: false },
  ],
  'post-create': [
    { key: 'publishingMethod', isExpanded: true },
    { key: 'postContent', isExpanded: true },
    { key: 'postPlanning', isExpanded: true },
    { key: 'localPosts', isExpanded: true },
    { key: 'communication', isExpanded: false },
    { key: 'boostBudget', isExpanded: false },
    { key: 'labels', isExpanded: false },
    { key: 'notes', isExpanded: false },
  ],
} as const;

export function useWidgetsSetupApi({ target }: Props) {
  const queryClient = useQueryClient();

  const key = queryKey.widgetsSetup({ target });

  const query = useQuery(key, () => {
    if (target === 'post-create') return DEFAULT_WIDGET_SETUP[target];
    return WidgetsSetupApi.get({ target });
  });

  const mutation = useMutation(
    (widgetsSetup: WidgetsSetup[] | null) =>
      WidgetsSetupApi.set({ target, widgetsSetup: widgetsSetup }),
    {
      onMutate: async (newWidgetsSetup: WidgetsSetup[] | null) => {
        await queryClient.cancelQueries(key);

        queryClient.setQueryData(key, newWidgetsSetup);
      },
    },
  );

  const setWidgetExpanded = (widgetKey: string, isExpanded: boolean) => {
    const currentSetup = query.data;
    if (!currentSetup) return;

    const newSetup = currentSetup.map((widget) =>
      widget.key === widgetKey ? { ...widget, isExpanded } : widget,
    );
    mutation.mutate(newSetup);
  };

  const getWidgetExpansionState = (): WidgetsExpansionState => {
    const currentSetup = query.data;
    if (!currentSetup) return {};

    return currentSetup.reduce((acc, widget) => {
      acc[widget.key] = {
        isExpanded: widget.isExpanded ?? false,
        setExpanded: (expanded: boolean) =>
          setWidgetExpanded(widget.key, expanded),
      };
      return acc;
    }, {} as WidgetsExpansionState);
  };

  return {
    query,
    mutation,
    DEFAULT_WIDGET_SETUP: DEFAULT_WIDGET_SETUP[target],
    getWidgetExpansionState,
    setWidgetExpanded,
  };
}
